"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import BullseyeIcon from "@/assets/icons/bullseye.svg";
import { cn } from "@/lib/utils";
import CurveGlow from "../../assets/curve-glow.svg";
import CurveLine from "../../assets/curve-line.svg";
import PointerGlow from "../../assets/pointer-glow.svg";
import Pointer from "../../assets/pointer.svg";
import { CardMode } from "../../target-card-state";
import { TARGET_AIRTIME_COLOR } from "../target-card-parts/target-card-parts";
import styles from "./gauge-bar.module.scss";
const SOV_HALF_SPAN = 15;
const MAJOR_STEP = 5;
const TARGET_REACHED_COLOR = "#0BD66A";
const TICK_STROKE_COLOR = "rgba(255,255,255,0.6)";
const ACTIVE_MS = 1200;
export function clampPercent(value: number) {
  return Math.min(100, Math.max(0, value));
}

/** Values in (min, max) on `step`, excluding the edges. */
function stepsInRange(min: number, max: number, step: number) {
  const values: number[] = [];
  const first = Math.ceil(min / step) * step;
  for (let p = first; p < max - 0.001; p += step) {
    if (p <= min + 0.001) continue;
    values.push(p);
  }
  return values;
}
function leftPct(p: number, chartMin: number, chartMax: number) {
  return (p - chartMin) / (chartMax - chartMin) * 100;
}
function buildSovAxis(currentSov: number, targetSov: number) {
  const currentPct = clampPercent(currentSov);
  const targetPct = clampPercent(targetSov);
  const chartMin = currentPct - SOV_HALF_SPAN;
  const chartMax = currentPct + SOV_HALF_SPAN;
  let majorPercents = stepsInRange(chartMin, chartMax, MAJOR_STEP);
  if (majorPercents.length < 2) {
    majorPercents = [Math.round(chartMin / MAJOR_STEP) * MAJOR_STEP, Math.round(chartMax / MAJOR_STEP) * MAJOR_STEP];
  }
  const majorSet = new Set(majorPercents);
  return {
    chartMin,
    chartMax,
    currentPct,
    targetPct,
    majorPercents,
    labelPercents: majorPercents.filter(p => p >= 0 && p <= 100 && p % 10 === 0),
    minorTicks: stepsInRange(chartMin, chartMax, 1).filter(p => !majorSet.has(p))
  };
}
function tickStrokeFor(mode: CardMode, isReached: boolean) {
  if (mode === CardMode.Pb) return TARGET_AIRTIME_COLOR;
  if (mode === CardMode.Target && isReached) return TARGET_REACHED_COLOR;
  return TICK_STROKE_COLOR;
}
function GaugeAxis(t0) {
  const $ = _c(24);
  const {
    currentSov,
    targetSov,
    isNoData,
    isReached,
    mode,
    isActive,
    hideTargetAxisLabel
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== currentSov || $[1] !== hideTargetAxisLabel || $[2] !== isActive || $[3] !== isNoData || $[4] !== isReached || $[5] !== mode || $[6] !== targetSov) {
    const {
      chartMin,
      chartMax,
      currentPct,
      targetPct,
      majorPercents,
      labelPercents,
      minorTicks
    } = buildSovAxis(currentSov, targetSov);
    const tickStroke = tickStrokeFor(mode, isReached);
    const toLeft = p => `${leftPct(p, chartMin, chartMax)}%`;
    t6 = isNoData ? styles.gaugeSvgNoData : styles.gaugeSvg;
    t7 = true;
    t1 = styles.tickGroup;
    t2 = minorTicks.map(p_0 => <div key={`minor-${p_0}`} className={styles.tickMinor} style={{
      left: toLeft(p_0),
      background: tickStroke
    }} />);
    t3 = isActive && minorTicks.map(p_1 => {
      const inWave = targetPct >= currentPct ? p_1 > currentPct - 3 && p_1 <= targetPct : p_1 < currentPct + 3 && p_1 >= targetPct;
      if (!inWave) {
        return null;
      }
      return <div key={`wave-${p_1}`} className={cn(styles.tickWave, styles.waveTick)} style={{
        left: toLeft(p_1),
        background: tickStroke,
        animationDelay: `${Math.abs(p_1 - currentPct) * 35}ms`
      }} />;
    });
    t4 = majorPercents.map(p_2 => <div key={`major-${p_2}`} className={styles.tickMajor} style={{
      left: toLeft(p_2),
      background: tickStroke
    }} />);
    t5 = labelPercents.map(p_3 => {
      if (hideTargetAxisLabel && Math.round(p_3) === Math.round(targetPct)) {
        return null;
      }
      return <span key={`label-${p_3}`} className={styles.axisLabel} style={{
        left: toLeft(p_3)
      }}>{isNoData ? "\u2014" : `${Math.round(p_3)}%`}</span>;
    });
    $[0] = currentSov;
    $[1] = hideTargetAxisLabel;
    $[2] = isActive;
    $[3] = isNoData;
    $[4] = isReached;
    $[5] = mode;
    $[6] = targetSov;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t8 = <div className={t1}>{t2}{t3}{t4}{t5}</div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t9 = <div className={t6} aria-hidden={t7}>{t8}</div>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
export type GaugeBarProps = Readonly<{
  currentSov: number;
  targetSov: number;
  isNoData: boolean;
  isReached: boolean;
  mode: CardMode;
}>;

/** Share-of-airtime gauge: curve, tick axis, and center pointer. */
export function GaugeBar(t0) {
  const $ = _c(33);
  const {
    currentSov,
    targetSov,
    isNoData,
    isReached,
    mode
  } = t0;
  const [active, setActive] = useState(false);
  const tapTimerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      if (tapTimerRef.current !== null) {
        window.clearTimeout(tapTimerRef.current);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const targetDelta = targetSov - currentSov;
  const isActive = active;
  const isContractReached = mode === CardMode.Target && isReached;
  const showTargetTick = !isNoData && (mode === CardMode.Target || mode === CardMode.Pb) && targetSov > 0 && Math.abs(targetDelta) <= SOV_HALF_SPAN;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setActive(true);
      if (tapTimerRef.current !== null) {
        window.clearTimeout(tapTimerRef.current);
      }
      tapTimerRef.current = window.setTimeout(() => {
        setActive(false);
        tapTimerRef.current = null;
      }, ACTIVE_MS);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const activateTap = t3;
  const t4 = isActive && styles.gaugeHostActive;
  let t5;
  if ($[3] !== t4) {
    t5 = cn(styles.gaugeHost, t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e => {
      if (e.pointerType === "mouse") {
        setActive(true);
      }
    };
    t7 = e_0 => {
      if (e_0.pointerType === "mouse") {
        setActive(false);
      }
    };
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  const t8 = isNoData ? styles.curveLineNoData : styles.curveLine;
  let t9;
  if ($[7] !== t8) {
    t9 = <CurveLine className={t8} preserveAspectRatio="none" aria-hidden={true} />;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== isNoData) {
    t10 = !isNoData && <div className={styles.curveGlowHost}><CurveGlow className={styles.curveGlow} preserveAspectRatio="none" aria-hidden={true} /></div>;
    $[9] = isNoData;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = showTargetTick && isActive;
  let t12;
  if ($[11] !== currentSov || $[12] !== isActive || $[13] !== isNoData || $[14] !== isReached || $[15] !== mode || $[16] !== t11 || $[17] !== targetSov) {
    t12 = <GaugeAxis currentSov={currentSov} targetSov={targetSov} isNoData={isNoData} isReached={isReached} mode={mode} isActive={isActive} hideTargetAxisLabel={t11} />;
    $[11] = currentSov;
    $[12] = isActive;
    $[13] = isNoData;
    $[14] = isReached;
    $[15] = mode;
    $[16] = t11;
    $[17] = targetSov;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== isNoData) {
    t13 = !isNoData && <><Pointer className={styles.pointer} aria-hidden={true} /><PointerGlow className={styles.pointerGlow} aria-hidden={true} /></>;
    $[19] = isNoData;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== isContractReached || $[22] !== showTargetTick || $[23] !== targetDelta || $[24] !== targetSov) {
    t14 = showTargetTick && <div className={isContractReached ? styles.targetTickReached : styles.targetTickPending} style={{
      left: `${50 + targetDelta / (SOV_HALF_SPAN * 2) * 100}%`
    }} aria-hidden={true}><div className={styles.targetTickBadge}><BullseyeIcon width={12} height={12} aria-hidden={true} /><span>{Math.round(targetSov)}%</span></div><span className={styles.targetTickStem} /></div>;
    $[21] = isContractReached;
    $[22] = showTargetTick;
    $[23] = targetDelta;
    $[24] = targetSov;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t10 || $[27] !== t12 || $[28] !== t13 || $[29] !== t14 || $[30] !== t5 || $[31] !== t9) {
    t15 = <div className={t5} onPointerEnter={t6} onPointerLeave={t7} onClick={activateTap}>{t9}{t10}{t12}{t13}{t14}</div>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t5;
    $[31] = t9;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}